import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import apiRequest, { ApiResponse } from "../axios/useApi";
import { UIContext } from "./UIProvider";
import { downloadReport, handleApiError } from "../lib/utils";
import {
  chartListingType,
  GeneralLedgerChartAccountListAccodianType,
  GeneralLedgerContextType,
  GeneralLedgerJournalAccodianType,
  JournalApiResponseType,
  LedgerType,
  ListingData,
  journalPaginationType,
} from "../pages/GeneralLedger/types";
import { format } from "date-fns";
import {
  enabledReports,
  formatAccountData,
  formateJournalData,
} from "../pages/GeneralLedger/components/GeneralHelper";

interface LedgerData {
  journal: GeneralLedgerJournalAccodianType[];
  chartofaccounts: GeneralLedgerChartAccountListAccodianType[];
}

export const GeneralLedgerContext = createContext<GeneralLedgerContextType>({
  chartofaccountsData: [],
  journalData: [],
  isLoading: false,
  activeTab: "chartofaccounts",
  isExpanded: () => false,
  toggleAccordion: () => {},
  handleTabChange: () => {},
  expandAllRows: () => {},
  collapseAllRows: () => {},
  areAllRowsExpanded: () => false,
  journalPagination: {
    page: 1,
    totalPage: 1,
    totalRecords: 0,
    pageSize: 10,
  },
  setJournalPagination: () => {},
  listingPagination: {
    page: 1,
    totalPage: 1,
    totalRecords: 0,
    pageSize: 10,
  },
  setListingPagination: () => {},
  chartListing: {
    isListingShow: false,
    listingId: "",
    balance: 0,
    initialId: "",
    idType: "",
  },
  handleChangeChartListing: () => {},
  listingData: [],
  isListingDateLoading: false,
  handleDownload: () => {},
  isDownloading: false,
  getJournalistData: async () => {},
  getChartsOfAccountsList: async () => {},
  getListingData: async () => {},
  currentDateRange: {},
  setCurrentDateRange: () => {},
  handleDateChange: () => {},
});

type Props = PropsWithChildren;

export const GeneralLedgerProvider = ({ children }: Props) => {
  const { onError, onSuccess, businessId } = useContext(UIContext);
  const [journalPagination, setJournalPagination] =
    useState<journalPaginationType>({
      page: 1,
      totalPage: 1,
      totalRecords: 0,
      pageSize: 10,
    });
  const [listingPagination, setListingPagination] =
    useState<journalPaginationType>({
      page: 1,
      totalPage: 1,
      totalRecords: 0,
      pageSize: 10,
    });
  const [data, setData] = useState<LedgerData>({
    journal: [],
    chartofaccounts: [],
  });

  const [isLoading, setIsLoading] = useState(false);
  // Add a flag to prevent duplicate API calls
  const [isApiCallInProgress, setIsApiCallInProgress] = useState(false);

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  // Track current date range
  const [currentDateRange, setCurrentDateRange] = useState<{
    startDate?: Date;
    endDate?: Date;
  }>({
    startDate: firstDayOfMonth,
    endDate: lastDayOfMonth,
  });

  const [activeTab, setActiveTab] = useState<LedgerType>("chartofaccounts"); // Default tab
  const [chartListing, setChartListing] = useState<chartListingType>({
    isListingShow: false,
    listingId: "",
    balance: 0,
    initialId: "",
    idType: "",
  });
  const [listingData, setListingData] = useState<ListingData[]>([]);
  const [isListingDateLoading, setIsListingDateLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [expandedItems, setExpandedItems] = useState<Record<string, string[]>>(
    () => {
      const initialState: Record<string, string[]> = {};
      enabledReports.forEach((report: any) => {
        initialState[report.type] = [];
      });
      return initialState;
    }
  );

  const getListingData = async (
    listingId: string,
    item?: GeneralLedgerChartAccountListAccodianType,
    pageNo: number = 1,
    pageSize: number = 10
  ) => {
    if (isApiCallInProgress) {
      return;
    }

    setIsApiCallInProgress(true);
    setIsListingDateLoading(true);

    try {
      if (!item || !businessId) {
        onError?.(new Error("Missing required data for listing"));
        setIsListingDateLoading(false);
        setIsApiCallInProgress(false);
        return;
      }

      // Use the stored ID type and value if available
      let queryParam = "";
      if (chartListing.initialId && chartListing.idType) {
        queryParam = `${chartListing.idType}=${chartListing.initialId}`;
      } else {
        // Map levels to correct ID types
        if (item.level === "parent") {
          queryParam = `categoryId=${item.categoryId || ""}`;
        } else if (item.level === "child") {
          queryParam = `accountSubTypeId=${item.accountSubTypeId || ""}`;
        } else if (item.level === "grandchild") {
          queryParam = `accountId=${item.accountId || ""}`;
        }
      }

      let url = `/Report/${businessId}/GeneralLedgerChartOfAccountsReport/${pageNo}?pageSize=${pageSize}&${queryParam}`;

      const response: ApiResponse<any> = await apiRequest({
        url,
        method: "GET",
      });

      if (response && response.success) {
        onSuccess?.(
          "GET",
          "GeneralLedgerChartOfAccountsReport",
          response?.message || "Listing data retrieved successfully!"
        );

        // Transform API response to match ListingData structure
        const formattedListingData: ListingData[] =
          response.data?.items?.map((item: any) => ({
            date: item.voucherDate,
            journalId: item.voucherId.toString(),
            source: item.transactionType || "Transaction",
            debit: item.debitAmt || 0,
            credit: item.creditAmt || 0,
            runningBalance: item.runningBalance || 0,
            externalId: item.externalId || "",
          })) || [];

        setListingData(formattedListingData);

        // Update listing pagination
        setListingPagination({
          page: response.data?.pageNumber || 1,
          totalPage: response.data?.totalPages || 1,
          totalRecords: response.data?.totalRecords || 0,
          pageSize: pageSize,
        });
      } else {
        handleApiError(response, onError);
        setListingData([]);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching Listing data."));
      setListingData([]);
    }

    setIsListingDateLoading(false);
    setIsApiCallInProgress(false);
  };

  const handleChangeChartListing = (
    listingState: boolean,
    listingId?: GeneralLedgerChartAccountListAccodianType
  ) => {
    // Ensure level is set for the clicked item
    if (listingId && !listingId.level) {
      if (
        listingId.name === listingId.type &&
        listingId.name === listingId.sub_type
      ) {
        listingId.level = "parent";
      } else if (listingId.children && listingId.children.length > 0) {
        listingId.level = "child";
      } else {
        listingId.level = "grandchild";
      }
    }

    // Determine which ID to use based on level
    let initialId = "";
    let idType = "";
    if (listingId) {
      if (listingId.level === "parent") {
        // Parent uses categoryId
        initialId = listingId.categoryId || "";
        idType = "categoryId";
      } else if (listingId.level === "child") {
        // Child uses accountSubTypeId
        initialId = listingId.accountSubTypeId || "";
        idType = "accountSubTypeId";
      } else if (listingId.level === "grandchild") {
        // Grandchild uses accountId
        initialId = listingId.accountId || "";
        idType = "accountId";
      }
    }

    setChartListing({
      isListingShow: listingState,
      listingId: listingId?.name || "",
      balance: Number(listingId?.balance) || 0,
      initialId,
      idType,
    });

    // Only call getListingData if we're showing the listing and we have a valid listingId
    if (listingState && listingId) {
      // Reset to page 1 when changing listing
      setListingPagination((prev) => ({ ...prev, page: 1 }));
      getListingData(listingId.name || "", listingId, 1, 10);
    } else {
      // Reset the listing data when returning to main view
      setListingData([]);
    }
  };

  const handleTabChange = (value: string) => {
    // Validate the tab type is one we recognize
    if (value !== "journal" && value !== "chartofaccounts") {
      onError?.(new Error("Unrecognized tab type:"));
      return;
    }

    if (value === "journal") {
      setListingData([]);
      setChartListing({
        isListingShow: false,
        listingId: "",
        balance: 0,
        initialId: "",
        idType: "",
      });
    }

    setActiveTab(value as LedgerType);
  };

  // Helper function to get all expandable item IDs for charts of accounts
  const getAllChartItemIds = (
    items: GeneralLedgerChartAccountListAccodianType[]
  ): string[] => {
    return items.reduce((ids: string[], item) => {
      // Only include items with children (expandable items)
      if (item.children && item.children.length > 0) {
        // Create identifier in the same format as accordion items
        const itemId = `${item.level || "parent"}_${item.name}`;
        ids.push(itemId);
        // Recursively get child IDs
        const childIds = getAllChartItemIds(
          item.children.map((child) => ({
            ...child,
            level: child.level || "child", // Ensure child level is set
          }))
        );
        ids.push(...childIds);
      }
      return ids;
    }, []);
  };

  // Helper function to get all expandable journal item IDs
  const getAllJournalItemIds = (
    items: GeneralLedgerJournalAccodianType[]
  ): string[] => {
    return items.reduce((ids: string[], item) => {
      // Only include items with children
      if (item.children && item.children.length > 0) {
        ids.push(item.id);
      }
      return ids;
    }, []);
  };

  // Get proper item IDs based on tab type
  const getAllItemIds = (tabId: LedgerType): string[] => {
    if (tabId === "chartofaccounts") {
      return getAllChartItemIds(data.chartofaccounts);
    } else {
      return getAllJournalItemIds(data.journal);
    }
  };

  const getChartsOfAccountsList = async (businessMasterId: string) => {
    setIsLoading(true);

    // Chart of accounts doesn't need date parameters
    try {
      // Build URL without any date range parameters
      let url = `/Report/${businessMasterId}/GeneralLedgerChartOfAccounts`;

      const response: ApiResponse<any> = await apiRequest({
        url,
        method: "GET",
      });

      if (response && response.success) {
        onSuccess?.(
          "GET",
          "getChartOfAccounts",
          response?.message || "Data retrieved successfully!"
        );
        const formatedData = formatAccountData(response?.data);
        setData({
          ...data,
          chartofaccounts: formatedData,
        });
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(
        new Error("An error occurred while fetching Chart of Accounts.")
      );
    }
    setIsLoading(false);

    // Return a promise to allow for .finally() chaining
    return Promise.resolve();
  };

  const getJournalistData = async (
    businessMasterId: string,
    pageNo: number,
    startDate?: Date,
    endDate?: Date
  ) => {
    setIsLoading(true);
    // Update current date range if provided
    if (startDate && endDate) {
      setCurrentDateRange({ startDate, endDate });
    }

    try {
      let url = `/Report/${businessMasterId}/GeneralLedgerJournalList/${pageNo}?pageSize=${journalPagination.pageSize}`;

      // Use provided dates or fallback to current date range
      const dateStart = startDate || currentDateRange.startDate;
      const dateEnd = endDate || currentDateRange.endDate;

      if (dateStart && dateEnd) {
        const formattedStartDate = format(dateStart, "yyyy-MM-dd");
        const formattedEndDate = format(dateEnd, "yyyy-MM-dd");
        url += `&FromDate=${formattedStartDate}&ToDate=${formattedEndDate}`;
      }

      const response: ApiResponse<JournalApiResponseType> = await apiRequest({
        url,
        method: "GET",
      });

      if (response && response.success) {
        onSuccess?.(
          "GET",
          "getJournal",
          response?.message || "Data retrieved successfully!"
        );
        const formattedJournalData = formateJournalData(
          response.data?.items || []
        );
        setData({
          ...data,
          journal: formattedJournalData,
        });

        setJournalPagination({
          page: response.data?.pageNumber || 1,
          totalPage: response.data?.totalPages || 1,
          totalRecords: response.data?.totalRecords || 0,
          pageSize: response.data?.pageSize || 10,
        });
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching journal data."));
    } finally {
      // Return a promise to allow for .finally() chaining
      setIsLoading(false);
      return Promise.resolve();
    }
  };

  useEffect(() => {
    // Load initial data when component mounts or when tab changes
    if (businessId && !isApiCallInProgress) {
      setIsApiCallInProgress(true);

      if (activeTab === "journal") {
        getJournalistData(
          businessId,
          1,
          firstDayOfMonth,
          lastDayOfMonth
        ).finally(() => {
          setIsApiCallInProgress(false);
        });
      } else if (activeTab === "chartofaccounts") {
        // For chart of accounts
        getChartsOfAccountsList(businessId).finally(() => {
          setIsApiCallInProgress(false);
        });
      }
    }
  }, [businessId, activeTab]);

  // useEffect to handle pagination changes
  useEffect(() => {
    // and if we're not already making an API call
    if (businessId && activeTab === "journal" && !isApiCallInProgress) {
      setIsLoading(true);
      setIsApiCallInProgress(true);

      // if (currentDateRange.startDate && currentDateRange.endDate) {
      getJournalistData(
        businessId,
        journalPagination.page,
        currentDateRange.startDate,
        currentDateRange.endDate
      ).finally(() => {
        setIsApiCallInProgress(false);
      });
      // }
      // else {
      //   const today = new Date();
      //   const firstDayOfMonth = new Date(
      //     today.getFullYear(),
      //     today.getMonth(),
      //     1
      //   );
      //   getJournalistData(
      //     businessId,
      //     journalPagination.page,
      //     firstDayOfMonth,
      //     today
      //   ).finally(() => {
      //     setIsApiCallInProgress(false);
      //     setIsLoading(false);
      //   });
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journalPagination.page, journalPagination.pageSize]);

  // Add an effect to handle listing pagination changes
  useEffect(() => {
    // Only trigger API calls for pagination changes, not when the listing is initially selected
    // handleChangeChartListing already calls getListingData for the initial selection
    if (businessId && chartListing.isListingShow && chartListing.listingId) {
      // Only respond to page changes here - the handleChangeChartListing
      // function will handle the initial data load
      if (listingPagination.page > 1) {
        // Find the item from chartofaccounts data and ensure level property is set
        const findItem = (
          items: GeneralLedgerChartAccountListAccodianType[]
        ): GeneralLedgerChartAccountListAccodianType | undefined => {
          for (const item of items) {
            if (item.name === chartListing.listingId) {
              // Ensure the level is set
              if (!item.level) {
                if (item.name === item.type && item.name === item.sub_type) {
                  item.level = "parent";
                } else if (item.children && item.children.length > 0) {
                  item.level = "child";
                } else {
                  item.level = "grandchild";
                }
              }
              return item;
            }
            if (item.children && item.children.length > 0) {
              const found = findItem(item.children);
              if (found) return found;
            }
          }
          return undefined;
        };

        const item = findItem(data.chartofaccounts);
        if (item) {
          // Use the pageSize from pagination state or default to 10
          const currentPageSize = listingPagination.pageSize || 10;
          getListingData(
            chartListing.listingId,
            item,
            listingPagination.page,
            currentPageSize
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, listingPagination.page, listingPagination.pageSize]);

  const toggleAccordion = (tabId: string, itemId: string) => {
    setExpandedItems((prev) => {
      const tabItems = [...(prev[tabId] || [])];
      let newExpandedItems;

      if (tabItems.includes(itemId)) {
        // Remove item from expanded items
        newExpandedItems = {
          ...prev,
          [tabId]: tabItems.filter((id) => id !== itemId),
        };
      } else {
        // Add item to expanded items
        newExpandedItems = { ...prev, [tabId]: [...tabItems, itemId] };

        // Check if all items are now expanded after adding this item
        const allIds = getAllItemIds(tabId as LedgerType);
        const newExpandedIds = [...tabItems, itemId];

        // If this is the last item to be expanded, trigger expand all to ensure all nested items are expanded
        if (allIds.length > 0 && newExpandedIds.length === allIds.length) {
          return { ...prev, [tabId]: allIds };
        }
      }

      return newExpandedItems;
    });
  };

  // Check if an accordion item is expanded
  const isExpanded = (tabId: string, itemId: string) => {
    return expandedItems[tabId]?.includes(itemId) || false;
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    if (activeTab === "chartofaccounts") {
      await downloadReport({
        data: data.chartofaccounts,
        fileName: "Chart_Of_Accounts",
        format: "excel",
        type: "chart-of-accounts",
      });
      onSuccess?.(
        "DOWNLOAD",
        "downloadJournal",
        "Report Downloaded Successfully!"
      );
      setIsDownloading(false);
    } else {
      try {
        // Build URL with date range parameters
        let url = `/Report/${businessId}/GeneralLedgerJournalList/${1}?pageSize=${
          journalPagination.totalRecords
        }`;

        // Include date range if available
        if (currentDateRange.startDate && currentDateRange.endDate) {
          const formattedStartDate = format(
            currentDateRange.startDate,
            "yyyy-MM-dd"
          );
          const formattedEndDate = format(
            currentDateRange.endDate,
            "yyyy-MM-dd"
          );
          url += `&FromDate=${formattedStartDate}&ToDate=${formattedEndDate}`;
        }

        const response: ApiResponse<JournalApiResponseType> = await apiRequest({
          url,
          method: "GET",
        });

        if (response && response.success) {
          const formattedJournalData = formateJournalData(
            response.data?.items || []
          );
          await downloadReport({
            data: formattedJournalData,
            fileName: "journal",
            format: "excel",
            type: "journal",
          });
          onSuccess?.(
            "DOWNLOAD",
            "downloadJournal",
            "Report Downloaded Successfully!"
          );
        } else {
          onError?.(new Error("An error occurred while downloading report."));
        }
      } catch (error) {
        onError?.(
          new Error("An error occurred while donwloading journal data.")
        );
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const expandAllRows = (tabId: LedgerType) => {
    const allItemIds = getAllItemIds(tabId);
    setExpandedItems((prev) => ({
      ...prev,
      [tabId]: allItemIds,
    }));
  };

  const collapseAllRows = (tabId: LedgerType) => {
    setExpandedItems((prev) => ({
      ...prev,
      [tabId]: [],
    }));
  };

  const areAllRowsExpanded = (tabId: LedgerType) => {
    const allExpandableIds = getAllItemIds(tabId);
    const expandedIds = expandedItems[tabId] || [];

    // Only consider items with children for the expandable state
    return (
      allExpandableIds.length > 0 &&
      allExpandableIds.every((id) => expandedIds.includes(id))
    );
  };

  const handleDateChange = (startDate: Date, endDate: Date) => {
    setCurrentDateRange({ startDate, endDate });
  };

  return (
    <GeneralLedgerContext.Provider
      value={{
        chartofaccountsData: data.chartofaccounts,
        journalData: data.journal,
        isLoading,
        activeTab,
        isExpanded,
        toggleAccordion,
        handleTabChange,
        expandAllRows,
        collapseAllRows,
        areAllRowsExpanded,
        journalPagination,
        setJournalPagination,
        listingPagination,
        setListingPagination,
        chartListing,
        handleChangeChartListing,
        listingData,
        isListingDateLoading,
        handleDownload,
        isDownloading,
        getJournalistData,
        getChartsOfAccountsList,
        getListingData,
        currentDateRange,
        setCurrentDateRange,
        handleDateChange,
      }}
    >
      {children}
    </GeneralLedgerContext.Provider>
  );
};
